import { useEffect, useState } from 'react';
import { decode } from 'blurhash';

import { buildImgixSrc } from 'shared/components/imgix';

// imgix recommends requesting blurhash at a small width (20-50px) — see
// https://docs.imgix.com/en-US/apis/rendering/format/output-format#blurhash
const SAMPLE_WIDTH = 32;
const SAMPLE_HEIGHT = 18;

// Normalized (x, y) points to sample color from, spread toward the corners and
// center so the resulting mesh gradient blends the image's real color palette.
const SAMPLE_POINTS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0.12, y: 0.2 },
  { x: 0.88, y: 0.15 },
  { x: 0.5, y: 0.5 },
  { x: 0.18, y: 0.85 },
  { x: 0.9, y: 0.82 },
];

function samplePixelColor(pixels: Uint8ClampedArray, x: number, y: number): string {
  const pixelX = Math.min(SAMPLE_WIDTH - 1, Math.floor(x * SAMPLE_WIDTH));
  const pixelY = Math.min(SAMPLE_HEIGHT - 1, Math.floor(y * SAMPLE_HEIGHT));
  const index = (pixelY * SAMPLE_WIDTH + pixelX) * 4;
  return `rgb(${pixels[index]}, ${pixels[index + 1]}, ${pixels[index + 2]})`;
}

// Module-scoped cache so multiple carousel mounts (or re-renders) sharing the same
// dispensary logo don't re-fetch/re-decode the same blurhash repeatedly. `inFlight`
// dedupes concurrent requests for the same URL made before the first one resolves.
const colorCache = new Map<string, string[] | null>();
const inFlight = new Map<string, Promise<string[] | null>>();

async function fetchMeshGradientColors(imageUrl: string): Promise<string[] | null> {
  if (colorCache.has(imageUrl)) {
    return colorCache.get(imageUrl) ?? null;
  }

  const existing = inFlight.get(imageUrl);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<string[] | null> => {
    try {
      // buildImgixSrc handles S3-to-Imgix URL conversion and normalizes invalid sources,
      // so this works whether logoImage is already an Imgix URL or a raw upload source.
      const blurhashUrl = buildImgixSrc(imageUrl, { fm: 'blurhash', w: SAMPLE_WIDTH });
      const response = await fetch(blurhashUrl);
      if (!response.ok) {
        throw new Error(`Unexpected status ${response.status} fetching blurhash`);
      }
      const hash = (await response.text()).trim();
      const pixels = decode(hash, SAMPLE_WIDTH, SAMPLE_HEIGHT);
      const colors = SAMPLE_POINTS.map(({ x, y }) => samplePixelColor(pixels, x, y));
      colorCache.set(imageUrl, colors);
      return colors;
    } catch {
      colorCache.set(imageUrl, null);
      return null;
    } finally {
      inFlight.delete(imageUrl);
    }
  })();

  inFlight.set(imageUrl, promise);
  return promise;
}

/**
 * Fetches an imgix blurhash string for the given image and samples a handful of
 * colors from the decoded pixels, for building a CSS "mesh gradient" (several
 * overlapping radial gradients) instead of rendering the blurhash as a bitmap.
 */
export function useMeshGradientColors(imageUrl?: string | null): string[] | null {
  const [colors, setColors] = useState<string[] | null>(() => (imageUrl ? colorCache.get(imageUrl) ?? null : null));

  useEffect(() => {
    if (!imageUrl) {
      setColors(null);
      return undefined;
    }

    let cancelled = false;

    void fetchMeshGradientColors(imageUrl).then((result) => {
      if (!cancelled) {
        setColors(result);
      }
    });

    return (): void => {
      cancelled = true;
    };
  }, [imageUrl]);

  return colors;
}
